import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Sparkles } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CredibilityStrip from "./CredibilityStrip";
import SEO, { SITE_URL } from "../SEO";
import { FaqSection } from "../FaqList";
import { FEATURES_PAGE_FAQS } from "../../data/pageFaqs";
import { PRODUCT_FAMILIES, COMPANY } from "../../data/knowledge";
import { Reveal, Overline } from "../Reveal";
import FeatureOrb, { MoreOrb } from "../FeatureOrb";
import { Button } from "../ui/button";
import { FEATURE_CATEGORIES, AUTOMATION_CATEGORIES, SOFTWARE_CATEGORIES, PLATFORM_CATEGORIES, featurePath } from "../../data/features";
import { SIGNUP_URL, PLAN_LOW_PRICE } from "../../data/content";

// Core products get their own SoftwareApplication node, pointing at their
// standalone detail page (FeatureDetailPage).
const CORE_PRODUCT_IDS = ["gmb", "review-routing", "facebook", "instagram", "whatsapp"];

const CORE_PRODUCT_SCHEMA = FEATURE_CATEGORIES.filter((c) =>
  CORE_PRODUCT_IDS.includes(c.id)
).map((c) => ({
  "@type": "SoftwareApplication",
  name: c.title,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web-based",
  url: `${SITE_URL}${featurePath(c)}`,
  description: c.intro,
  ...(c.image ? { image: `${SITE_URL}${c.image}` } : {}),
  publisher: { "@type": "Organization", name: "MagicBolt AI", url: SITE_URL },
  offers: {
    "@type": "AggregateOffer",
    priceCurrency: "INR",
    lowPrice: PLAN_LOW_PRICE,
    offerCount: 3,
    url: `${SITE_URL}/pricing`,
  },
  // TODO: needs real data - no `aggregateRating` until verified ratings exist.
}));

// One ListItem per feature category so answer engines can enumerate and
// cite the full capability list, plus a SoftwareApplication per core product.
const FEATURES_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ItemList",
      name: "MagicBolt AI Features",
      description:
        "Every automation and software capability included in the MagicBolt AI white-label platform.",
      itemListElement: FEATURE_CATEGORIES.map((cat, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Service",
          name: cat.title,
          description: cat.intro,
          url: `${SITE_URL}${featurePath(cat)}`,
        },
      })),
    },
    ...CORE_PRODUCT_SCHEMA,
  ],
};

export function AccentBadge({ accent, size = "w-16 h-16", ic = "w-8 h-8" }) {
  return (
    <span
      className={`grid place-items-center ${size} rounded-xl shrink-0`}
      style={accent.gradient ? { background: accent.gradient } : { background: accent.tint }}
    >
      <accent.Icon className={ic} style={{ color: accent.color }} />
    </span>
  );
}

export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-[#F4F6FB]" data-testid="features-page">
      <SEO
        title="Features - AI Automation, AEO & GEO Tools | MagicBolt AI"
        description="Every MagicBolt AI feature: Google Business Profile, Facebook, Instagram, WhatsApp and WordPress automation, AEO & GEO, QR reviews, menus and bookings."
        path="/features"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Features", path: "/features" },
        ]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FEATURES_JSON_LD)}</script>
      </Helmet>
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 pb-16 overflow-hidden bg-white border-b border-[#E6E9F1]">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-24 right-0" />
        <div className="mb-blob mb-blob-blue mb-anim-b w-[320px] h-[320px] top-10 -left-20" />
        <div className="absolute inset-0 mb-grid-lines opacity-60" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E6E9F1] bg-white px-4 py-1.5 mb-6 shadow-[0_2px_10px_rgba(11,21,51,0.05)]">
              <Sparkles className="w-3.5 h-3.5 text-[#FF6B00]" />
              <span className="text-xs font-medium text-[#475569]">Everything MagicBolt AI automates</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-none text-[#0B1533]">
              Features built to{" "}
              <span className="mb-gradient-text">run agencies on autopilot</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">
              MagicBolt AI is an all-in-one AI automation platform for agencies.
              From Google, social and WhatsApp to QR, reviews and menus - every repetitive
              task across the customer journey, automated end to end.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="features-header-cta"
                size="lg"
                className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg h-12 px-7 text-base hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(255,107,0,0.35)]"
                asChild
              >
                <a href={SIGNUP_URL}>Start Free Trial <ArrowRight className="ml-2 w-4 h-4" /></a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-[#CBD2E0] bg-white text-[#0B1533] hover:bg-white hover:text-[#0B1533] font-semibold rounded-lg h-12 px-7 text-base"
                asChild
              >
                <Link to="/pricing">View Pricing</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature grid - each tile opens its feature page */}
      <section className="bg-[#F4F6FB] border-b border-[#E6E9F1]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8 py-14 md:py-16">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <Overline>Agency OS</Overline>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tighter leading-tight text-[#0B1533]">
                <span className="text-[#FF6B00]">Automation Tools</span>, fully white-label
              </h2>
              <p className="mt-4 text-base md:text-lg text-[#475569] leading-relaxed">
                Every MagicBolt AI automation channel across the customer journey, each one
                yours to resell. Pick any card to see everything it does.
              </p>
            </div>
            <div
              data-testid="feature-poster-grid"
              className="mt-10 grid grid-cols-2 gap-x-4 gap-y-10 lg:grid-cols-4 lg:gap-x-8"
            >
              {AUTOMATION_CATEGORIES.map((cat) => (
                <FeatureOrb key={cat.id} cat={cat} big testId="feature-poster-big" />
              ))}
            </div>

            <div className="mx-auto mt-20 max-w-2xl text-center">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tighter leading-tight text-[#0B1533]">
                Run every client from <span className="text-[#FF6B00]">one white-label platform</span>
              </h2>
              <p className="mt-4 text-base md:text-lg text-[#475569] leading-relaxed">
                Manage clients under your own brand, publish to every channel from one
                composer, and let AI create content on autopilot.
              </p>
            </div>
            <div
              data-testid="feature-platform-grid"
              className="mx-auto mt-10 grid max-w-5xl grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-3"
            >
              {PLATFORM_CATEGORIES.map((cat) => (
                <FeatureOrb key={cat.id} cat={cat} big testId="feature-poster-big" />
              ))}
            </div>

            <div className="mx-auto mt-20 max-w-2xl text-center">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tighter leading-tight text-[#0B1533]">
                Included with Powerful Software
              </h2>
              <p className="mt-4 text-base md:text-lg text-[#475569] leading-relaxed">
                Standalone software products bundled in at no extra cost - that agencies
                can sell to clients as their own.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 lg:grid-cols-4 lg:gap-x-8">
              {SOFTWARE_CATEGORIES.filter((cat) => !cat.platform).map((cat) => (
                <FeatureOrb key={cat.id} cat={cat} testId="feature-poster" />
              ))}
              <MoreOrb to="/pricing" label="See it all in pricing" testId="feature-poster-more" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 border-t border-[#E6E9F1] overflow-hidden bg-white">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-20 left-1/2 -translate-x-1/2" />
        <div className="absolute inset-0 mb-radial-glow" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter leading-none text-[#0B1533]">
              See it all working for your agency
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#475569] max-w-xl mx-auto leading-relaxed">
              Every feature, one platform, fully white-label. Start your free trial today.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg h-12 px-7 text-base hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(255,107,0,0.35)]"
                asChild
              >
                <a href={SIGNUP_URL}>Start Free Trial <ArrowRight className="ml-2 w-4 h-4" /></a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-[#CBD2E0] bg-white text-[#0B1533] hover:bg-white hover:text-[#0B1533] font-semibold rounded-lg h-12 px-7 text-base"
                asChild
              >
                <Link to="/pricing">Compare Plans</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How the products fit together - plain-text overview of the families */}
      <section className="py-20 md:py-24 bg-white border-t border-[#E6E9F1]" data-testid="features-overview">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-8">
          <Overline>How it fits together</Overline>
          <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tighter text-[#0B1533]">
            How do MagicBolt AI's products work together?
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#475569] leading-relaxed">{COMPANY.summary}</p>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {PRODUCT_FAMILIES.map((f) => (
              <div key={f.id} className="rounded-2xl border border-[#E6E9F1] bg-[#F4F6FB] p-6">
                <h3 className="text-base font-bold text-[#0B1533]">{f.name}</h3>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FaqSection title="Frequently asked questions about MagicBolt AI features" items={FEATURES_PAGE_FAQS} tinted>
        <p className="mt-6 text-sm">
          <Link to="/product-finder" className="font-semibold text-[#FF6B00] hover:underline">
            Not sure what you need? Try the Product Finder
          </Link>
        </p>
      </FaqSection>

      <CredibilityStrip />

      <Footer showCta={false} />
    </div>
  );
}
